import { memo } from 'react';

import { CgCrown } from 'react-icons/cg';
import { FiTrendingUp } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';

import { useEventfy, useGamificationQuery } from '@hooks';
import { selectIsGamificationRankingEnabled } from '@hooks/useEventfy/selectors';
import { Avatar } from '@components';

import * as S from './styles';

const medalColors: Record<number, string> = {
  2: '#C0C0C0',
  3: '#CD7F32',
};

const RankingPanel = () => {
  const isGamificationEnabled = useEventfy(selectIsGamificationRankingEnabled);
  const gamificationQuery = useGamificationQuery();
  const { t } = useTranslation();

  if (!isGamificationEnabled) return null;
  if (!gamificationQuery.isSuccess) return null;

  const data = gamificationQuery.data ?? [];

  if (!data.length) {
    return (
      <S.Container>
        <S.Header>
          <S.HeaderLeft>
            <CgCrown size={14} />
            <h3>{t('pages.lobby.ranking')}</h3>
          </S.HeaderLeft>
        </S.Header>
        <S.EmptyState>
          <CgCrown size={34} />
          <strong>Nenhum ranking por enquanto</strong>
          <span>
            Participe de atividades e eventos para conquistar sua posição.
          </span>
        </S.EmptyState>
      </S.Container>
    );
  }

  const rankings = data.slice(0, 3);
  const top1 = rankings[0];
  const runners = rankings.slice(1);

  return (
    <S.Container>
      <S.Header>
        <S.HeaderLeft>
          <CgCrown size={14} />
          <h3>{t('pages.lobby.ranking')}</h3>
        </S.HeaderLeft>
        <S.LeaderboardLink to="/ranking">
          Ver ranking completo
        </S.LeaderboardLink>
      </S.Header>

      <S.TopCard>
        <S.TopCardGlow />
        <S.TopCardContent>
          <S.TopAvatarWrapper>
            <S.TopAvatar>
              <Avatar src={top1.user.data.avatar} alt={top1.user.data.name} />
            </S.TopAvatar>
            <S.RankBadge>1</S.RankBadge>
          </S.TopAvatarWrapper>
          <S.TopInfo>
            <div>{top1.user.data.name}</div>
            <div>{top1.info_user}</div>
          </S.TopInfo>
          <S.TopPoints>
            <div>{top1.total} pts</div>
            <S.TrendUp>
              <FiTrendingUp size={8} />
            </S.TrendUp>
          </S.TopPoints>
        </S.TopCardContent>
      </S.TopCard>

      {runners.map((ranking, index) => {
        const position = index + 2;
        return (
          <S.RunnerCard key={`ranking-${ranking.user_id}`}>
            <S.RunnerRank $color={medalColors[position] || '#94a3b8'}>
              {position}
            </S.RunnerRank>
            <S.RunnerAvatar>
              <Avatar
                src={ranking.user.data.avatar}
                alt={ranking.user.data.name}
              />
            </S.RunnerAvatar>
            <S.RunnerInfo>
              <div>{ranking.user.data.name}</div>
              <div>{ranking.info_user}</div>
            </S.RunnerInfo>
            <S.RunnerPoints>
              <div>{ranking.total} pts</div>
              <S.TrendUp>
                <FiTrendingUp size={7} />
              </S.TrendUp>
            </S.RunnerPoints>
          </S.RunnerCard>
        );
      })}
    </S.Container>
  );
};

export default memo(RankingPanel);
